<template lang="pug">
#draw.draw.u-ltr

  .draw-wrapper
    canvas#canvas
    #cursor.cursor
    .draw-nav
      .draw-nav-item
        AppLink(to="/").draw-nav-logo
          AppIcon(name="logoIcon")
      .draw-nav-item
        .draw-nav-points {{ history.length }}
      .draw-nav-item
        .draw-nav-item-icon(@click='removeHistoryItem' :class="{ 'is-disabled': !history.length }" title='Undo')
          AppIcon(name="undo")
      .draw-nav-item
        .draw-nav-item-icon(@click='removeAllHistory' :class="{ 'is-disabled': !history.length }" title='Clear all')
          AppIcon(name="delete")
      .draw-nav-item.has-subs
        .draw-nav-item-icon( title='Brush options' @click.prevent="navToggle")
          AppIcon(name="edit")
        .draw-nav-item-overlay
          p.u-mb-ts Options
          ul.draw-nav-item-overlay-list
            li(title='Restrict movement vertical' @click='options.restrictY = !options.restrictY; options.restrictX = false', :class='{ "is-active": options.restrictY }')
              AppIcon(name="arrow-forward")
              span Restrict vertical
            li(title='Restrict horizontal' @click='options.restrictX = !options.restrictX; options.restrictY = false', :class='{ "is-active": options.restrictX }')
              AppIcon(name="arrow-upward")
              span Restrict horizontal
            li(title='Simplify paths' @click='simplify', :class='{ "is-disabled": !history.length }')
              AppIcon(name="simple")
              span Simplify paths
            li(title='Go nutz' @click='jumble', :class='{ "is-disabled": !history.length }')
              AppIcon(name="shuffle")
              span Go nutz

      .draw-nav-item.has-subs
        .draw-nav-item-icon( title='Pick a brush size' @click.prevent="navToggle")
          AppIcon(name="sizes")
          span {{ size }}
        .draw-nav-item-overlay
          p.u-mb-ts Brush size
          ul.draw-nav-item-overlay-list.is-circle
            li(:title='sizeItem' v-for='sizeItem in sizes' @click="size = sizeItem" :style="{width: sizeItem + 'px', height: sizeItem + 'px'}" :class="{'is-active': size === sizeItem}")

      .draw-nav-item.has-subs
        .draw-nav-item-icon( title='Pick a color' @click.prevent="navToggle")
          AppIcon(name="color")
          span.is-color(:style='{backgroundColor: color}')
        .draw-nav-item-overlay.is-colorPricker
          p.u-mb-ts Brush color
          no-ssr
            chrome-picker(v-model="color2" @input="color = color2.hex" disableAlpha)

      .draw-nav-item
        .draw-nav-item-icon(@click.prevent="download"  title="Save")
          AppIcon(name="save")




</template>

<script>
import { generateSeoMeta } from '~/utils/seo';
import { Draw } from '~/plugins/tools/draw';
import { Chrome } from 'vue-color';

let draw;

let components = {
  'chrome-picker': Chrome
};

export default {
  layout: 'minimal',
  head: generateSeoMeta({ title: 'pageTitle', description: 'pageDesc' }, { i18n: true }),
  components: components,
  data: () => ({
    history: [],
    color: '#1a3aff',
    color2: '',
    options: {
      restrictY: false,
      restrictX: false
    },
    save: {
      name: '',
      saveItems: []
    },
    size: 12,
    sizes: [6, 12, 24, 48],
    weights: [2, 4, 6]
  }),
  methods: {
    navToggle(event) {
      if (event) {
        if (event.target.closest('.has-subs:not(.is-active)')) {
          const allSubs = document.querySelectorAll('.has-subs');
          // eslint-disable-next-line
          for (const sub of allSubs) {
            sub.classList.remove('is-active');
          }
        }

        event.target.closest('.has-subs').classList.toggle('is-active');
      }
    },
    download() {
      let canvas = document.getElementById('canvas');
      let image = canvas.toDataURL('image/png', 1.0).replace('image/png', 'image/octet-stream');
      let link = document.createElement('a');
      link.download = 'my-drawing.png';
      link.href = image;
      link.click();
    },
    removeHistoryItem() {
      this.history.splice(this.history.length - 2, 1);
      draw.redraw();
    },
    removeAllHistory() {
      this.history = [];
      draw.redraw();
    },
    simplify() {
      var simpleHistory = [];
      this.history.forEach((item, i) => {
        if (i % 6 !== 1 || item.isDummy) {
          simpleHistory.push(item);
        }
      });
      this.history = simpleHistory;
      draw.redraw();
    },
    jumble() {
      this.history.forEach((item, i) => {
        item.r += Math.sin(i * 20) * 5;
      });
      this.history = this.shuffle(this.history);
      draw.redraw();
    },
    shuffle(a) {
      var b = [];

      a.forEach(item => {
        if (!item.isDummy) {
          var l = b.length;
          var r = Math.floor(l * Math.random());
          b.splice(r, 0, item);
        }
      });

      for (var i = 0; i < b.length; i++) {
        if (i % 20 === 1) {
          b.push(draw.getDummyItem());
        }
      }

      return b;
    },
    saveItem() {
      if (this.save.name.length > 2) {
        var historyItem = {
          history: this.history.slice(),
          name: this.save.name
        };

        this.save.saveItems.push(historyItem);
        this.save.name = '';
      }
    },
    loadSave(item) {
      this.history = item.history.slice();
      draw.redraw();
    }
  },
  mounted() {
    draw = new Draw(this);
  }
};
</script>

<i18n>
{
  "en": {
    "pageTitle":"Draw",
    "pageDesc":"Test your color combinations and palettes. Calculate the contrast ratio to give your design depth and personality."
  },
  "ar": {
    "pageTitle":"ارسم",
    "pageDesc":"اختبر تركيبات الألوان واحسب التباين لإعطاء عمق للتصميم والشخصية من خلال الظلال وامنح تصميمك الروح واجعلها نابضة بالحياة"
  }
}
</i18n>

<style lang="stylus" scoped>
.draw
  position: fixed
  top: 0
  right: 0
  left: 0
  bottom: 0

  &-nav
    position: fixed
    z-index: 5
    bottom: 0
    top: 0
    left: 0
    width: 80px
    background-color: $clBlack
    display: flex
    flex-direction: column
    align-items: center
    -webkit-user-select: none
    -moz-user-select: none
    -ms-user-select: none
    user-select: none

    &-item
      display: flex
      width: 100%
      justify-content: center
      padding: 15px 0
      position: relative

      &:first-child
        padding-top: 20px

      &:last-child
        padding-bottom: 20px

      &-icon
        cursor: pointer
        font-size: 20px
        color: $clWhite
        position: relative

        &.is-disabled
          opacity: 0.5

        &:hover
          color: $clPrimary

        span
          position: absolute
          top: -5px
          right: -8px
          font-size: 10px

          &.is-color
            width: 8px
            height: 8px
            border-radius: 20em

      &.is-active
        .draw-nav-item-overlay
          display: block

      &-overlay
        position: absolute
        top: 50%
        left: 100%
        background: $clBlack
        color: $clWhite
        transform: translateY(-50%)
        width: 300px
        box-shadow: $boxShadow
        padding: 20px
        display: none

        &.is-colorPricker
          width: auto

        &-list
          list-style-type: none
          font-size: 18px

          >li
            cursor: pointer
            display: flex
            align-items: center

            &:not(:last-child)
              margin-bottom: 10px

            &.is-active
              color: $clPrimary

            &.is-disabled
              opacity: 0.5

            .icon
              margin-right: 10px

          &.is-circle
            display: flex
            flex-wrap: wrap
            justify-content: space-around
            align-items: center

            li
              width: 20px
              height: 20px
              border-radius: 20em
              background: $clWhite
              margin-bottom: 0

              &.is-active
                background: $clPrimary

    &-logo
      margin: 20px 0 40px

      .icon
        fill: $clWhite
        width: 40px
        height: 35px

    &-points
      width: 34px
      height: 34px
      background: $clWhite
      color: $clBlack
      border-radius: 20em
      display: flex
      align-items: center
      justify-content: center
      font-size: 14px

.cursor
  position: fixed
  top: 0
  left: 0
  width: 20px
  height: 20px
  border-radius: 50%
  border: 3px solid #1e1e1e
  pointer-events: none
  -webkit-user-select: none
  -moz-user-select: none
  -ms-user-select: none
  user-select: none
  mix-blend-mode: difference
  opacity: 0
  transition: opacity 1s

canvas
  width: calc(100% - 80px)
  height: 100vh
  background-color: $clWhite
  cursor: none

canvas:hover + .cursor
  opacity: 1

canvas:active + .cursor
  border-color: #3c3c3c
</style>
